
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/feature/LanguageSwitcher';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.services'), path: '/services' },
    //{ name: isRTL ? 'المنتجات' : 'Products', path: '/products' },
    { name: t('nav.portfolio'), path: '/portfolio' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white ${
          isScrolled 
            ? 'shadow-lg shadow-black/5' 
            : 'shadow-md'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer group">
              <img 
                src="/alebel-logo-transbg.png" 
                alt="ALEBEL Investment" 
                className="h-20 md:h-28 w-auto transition-transform duration-300 group-hover:scale-105"              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-5 py-2 font-medium text-sm tracking-wide transition-all duration-300 cursor-pointer rounded-lg ${
                    location.pathname === link.path
                      ? 'text-gold bg-gold/10' 
                      : 'text-charcoal hover:text-gold hover:bg-gold/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Button & Language Switcher */}
            <div className="hidden lg:flex items-center gap-4">
              <a
              href="/Alebel Investment Profile (12).pdf"
  target="_blank"
  rel="noopener noreferrer"
  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gold/30 text-navy text-sm font-medium hover:border-gold hover:bg-gold/5 transition-all duration-300"
>
  <i className="ri-file-download-line text-gold text-lg"></i>
  <span className="hidden md:inline">Company Profile</span>
</a>
<LanguageSwitcher />
              <Link
                to="/invest"
                className="px-6 py-3 bg-gradient-to-r from-gold to-gold-dark text-navy font-semibold text-sm rounded-lg hover:shadow-lg hover:shadow-gold/20 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <span>{t('nav.startInvesting')}</span>
                <i className="ri-arrow-right-up-line rtl:rotate-90"></i>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-12 h-12 flex items-center justify-center cursor-pointer rounded-lg hover:bg-cream transition-colors z-50"
            >
              <i className={`text-2xl transition-transform duration-300 ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-charcoal`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      ></div>

      {/* Mobile Menu Slide Panel */}
      <div 
        className={`fixed top-0 ${isRTL ? 'left-0' : 'right-0'} h-full w-[85%] max-w-[320px] bg-white z-50 lg:hidden transform transition-transform duration-500 ease-out shadow-2xl ${
          isMobileMenuOpen 
            ? 'translate-x-0' 
            : isRTL ? '-translate-x-full' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <img 
                src="/alebel-logo-transbg.png" 
                alt="ALEBEL Investment" 
                className="h-16 w-auto"
              />
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg hover:bg-cream transition-colors"
            >
              <i className="ri-close-line text-2xl text-charcoal"></i>
            </button>
          </div>

          {/* Mobile Menu Links */}
          <div className="flex-1 overflow-y-auto py-6 px-4">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-4 font-medium rounded-xl transition-all duration-300 cursor-pointer text-base ${
                    location.pathname === link.path
                      ? 'text-gold bg-gold/10'
                      : 'text-charcoal hover:text-gold hover:bg-gold/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Language Switcher in Mobile */}
            <div className="mt-6 px-4 py-4 border-t border-gray-100">
              <LanguageSwitcher />
            </div>
          </div>

          {/* Mobile Menu Footer */}
          <div className="p-6 border-t border-gray-100">
            <Link
              to="/invest"
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-full px-6 py-4 bg-gradient-to-r from-gold to-gold-dark text-navy font-semibold rounded-xl text-center cursor-pointer block"
            >
              {t('nav.startInvesting')}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
